// SPDX-License-Identifier: AGPL-3.0-or-later
import {safePause} from '@app/features/channel/components/GifVideoPool';
import {Platform} from '@app/features/platform/types/Platform';
import {useEffect, useRef} from 'react';

const BYPASS_BLOB_URL_PATH = Platform.isIOSWeb;
interface GifVideoPoolLike {
	getElement: (key: string) => HTMLVideoElement;
	getBlobUrl: (key: string) => Promise<string>;
	registerActive: (video: HTMLVideoElement) => void;
	unregisterActive: (video: HTMLVideoElement) => void;
	poolElement: (video: HTMLVideoElement, key: string) => void;
	isGloballyPaused?: () => boolean;
}
export function usePooledVideo({
	src,
	containerRef,
	videoPool,
	autoPlay,
	enabled = true,
}: {
	src: string | null | undefined;
	containerRef: React.RefObject<HTMLDivElement | null>;
	videoPool: GifVideoPoolLike;
	autoPlay: boolean;
	enabled?: boolean;
}) {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	useEffect(() => {
		if (!enabled) return;
		if (!src) return;
		const container = containerRef.current;
		if (!container) return;
		let cancelled = false;
		let attached = false;
		const video = videoPool.getElement(src);
		videoRef.current = video;
		videoPool.registerActive(video);
		attached = true;
		const isPausedByDefault = !autoPlay || (videoPool.isGloballyPaused?.() ?? false);
		const run = async () => {
			if (BYPASS_BLOB_URL_PATH) {
				if (video.src !== src) video.src = src;
			} else {
				try {
					const blobUrl = await videoPool.getBlobUrl(src);
					if (cancelled) return;
					if (video.src !== blobUrl) video.src = blobUrl;
				} catch {
					if (cancelled) return;
					if (video.src !== src) video.src = src;
				}
			}
			if (cancelled) return;
			const currentContainer = containerRef.current;
			if (!currentContainer) return;
			currentContainer.appendChild(video);
			if (isPausedByDefault || !autoPlay || (videoPool.isGloballyPaused?.() ?? false)) {
				safePause(video);
			}
		};
		void run();
		return () => {
			cancelled = true;
			if (attached) {
				videoPool.unregisterActive(video);
			}
			safePause(video);
			try {
				video.currentTime = 0;
			} catch {}
			videoPool.poolElement(video, src);
			if (videoRef.current === video) {
				videoRef.current = null;
			}
		};
	}, [src, enabled, containerRef, videoPool, autoPlay]);
	return videoRef;
}
